/**
 * React wrapper around `createPlayer` (§3 file plan: "kid / preview modes").
 *
 * It owns exactly three things — mount/unmount, the host's size, and the state
 * before the player exists (fetching `bundle.json`, and the friendly page when
 * that fails). Everything a child or a grown-up touches once the player is up
 * belongs to `player/hud.ts`; this component renders no controls of its own and
 * never reaches into the scene graph.
 *
 * The mounting itself is `player/standalone.ts#mountPlayer`, shared with
 * `player.html`, so the SPA and an exported zip wire the player identically.
 */
import { useCallback, useEffect, useRef, useState } from 'react'

import { bundleUrl as storyBundleUrl } from '@/api/stories'
import { evictPrefetched, prefetchBundle, takePrefetched, warmPlayerCode, type PrefetchOptions } from '@/player/prefetch'
import { describeFailure, fetchBundle, mountPlayer } from '@/player/standalone'

import type { FriendlyError, MountedPlayer } from '@/player/standalone'

export interface PrefetchStoryOptions extends PrefetchOptions {
  /**
   * Drop a copy fetched earlier and ask again. A story that streamed may have been prefetched
   * while it was still being made, and that revision (an open stream, the streamed parts) must not
   * open the finished story: a replay gets the final bundle.
   */
  fresh?: boolean
}

/**
 * Get a story ready to open before anyone asks: its `bundle.json`, the pack pages and plate its
 * first scene draws with, and the player's own lazily loaded code. Call it when an open is likely
 * (a pointer resting on "Play", the story's own page); the open that follows then starts from the
 * browser's caches. Cheap to repeat, never throws.
 */
export function prefetchStory(uid: string, { fresh = false, ...opts }: PrefetchStoryOptions = {}): void {
  if (typeof window === 'undefined') return
  warmPlayerCode()
  const url = new URL(storyBundleUrl(uid), window.location.href)
  // Taken is forgotten (`takePrefetched` hands a copy out once), so the prefetch below asks anew.
  if (fresh) takePrefetched(url.href)
  void prefetchBundle(url, opts).catch(() => undefined)
}

/** Drop any prefetched bundle in memory for this story. */
export function evictStoryPrefetch(uid: string): void {
  if (typeof window === 'undefined') return
  evictPrefetched(new URL(storyBundleUrl(uid), window.location.href))
}

/** A pointer that rests this long on a story is heading for "Play" (a pass over the list is not). */
const PLAY_INTENT_MS = 120
let intentTimer: ReturnType<typeof setTimeout> | null = null

/**
 * Props for a story's card that prefetch it (`prefetchStory`) when an open looks likely: a mouse
 * resting on it, keyboard focus inside it, or a finger coming down on it (the tap is ~100 ms away).
 * A finger fetches only the bundle: most touches on a list start a scroll, not an open, and the
 * first scene's pictures and voice are ~0.5 MB a card.
 */
export function playIntentProps(uid: string) {
  const cancel = (): void => {
    if (intentTimer !== null) clearTimeout(intentTimer)
    intentTimer = null
  }
  return {
    onPointerEnter: (e: { pointerType?: string }) => {
      if (e.pointerType === 'touch') return
      cancel()
      intentTimer = setTimeout(() => {
        intentTimer = null
        prefetchStory(uid)
      }, PLAY_INTENT_MS)
    },
    onPointerLeave: cancel,
    onPointerDown: (e: { pointerType?: string }) => prefetchStory(uid, e.pointerType === 'touch' ? { warm: false } : {}),
    onFocus: () => prefetchStory(uid),
  }
}

export interface StoryPlayerProps {
  /** Story uid — the bundle URL is derived from it. */
  uid?: string
  /** An explicit bundle URL; wins over `uid` (export previews, fixtures). */
  src?: string
  /**
   * `kid` fills the viewport and hides every studio affordance.
   * `preview` stays inside the page, for the timeline editor.
   */
  mode?: 'kid' | 'preview'
  /** The grown-up panel's Exit. */
  onExit?: () => void
  onEnded?: () => void
  /**
   * A story still being made will get no more parts, although its `bundle.json` does not say so
   * yet: its job ended without closing the stream (cancelled, or lost with its worker in a
   * restart). The player plays the parts it has, stops polling, then ends with "This story stopped
   * early" instead of waiting for the next part for good (`PlayerHandle.endStream`).
   */
  streamEnded?: boolean
  className?: string
}

export function StoryPlayer({ uid, src, mode = 'kid', onExit, onEnded, streamEnded = false, className }: StoryPlayerProps) {
  const hostRef = useRef<HTMLDivElement | null>(null)
  const mountedRef = useRef<MountedPlayer | null>(null)
  const [ready, setReady] = useState(false)
  // True once `mountPlayer` has started: it builds the HUD synchronously, and the HUD's start
  // screen ("Getting the story ready…" + the progress bar) is from then on the one status layer.
  // Keeping this overlay up until `createPlayer` resolved drew the same words twice, one set
  // showing through the other's scrim.
  const [handedOff, setHandedOff] = useState(false)
  const [failure, setFailure] = useState<FriendlyError | null>(null)
  const [attempt, setAttempt] = useState(0)

  // Callbacks travel through a ref so a parent re-render never tears the player
  // down and rebuilds it.
  const handlers = useRef({ onExit, onEnded })
  handlers.current = { onExit, onEnded }

  const href = src ?? (uid ? storyBundleUrl(uid) : null)

  useEffect(() => {
    const host = hostRef.current
    if (!host || !href) return

    let disposed = false
    let mounted: MountedPlayer | null = null
    const abort = new AbortController()

    setReady(false)
    setHandedOff(false)
    setFailure(null)

    const url = new URL(href, window.location.href)
    // The renderer's code downloads beside `bundle.json`, not after it.
    warmPlayerCode()
    void (async () => {
      try {
        const bundle = await fetchBundle(url, abort.signal)
        if (disposed) return
        setHandedOff(true)
        const m = await mountPlayer(host, bundle, {
          bundleUrl: url.href,
          ui: mode,
          // The story plays through like a video: a long story no longer stops at every part
          // for a tap. Tap-to-continue is still one switch away in the grown-up panel.
          player: { mode: 'read_to_me' },
          onExit: () => handlers.current.onExit?.(),
        })
        if (disposed) {
          m.destroy()
          return
        }
        mounted = m
        mountedRef.current = m
        m.player.on('ended', () => handlers.current.onEnded?.())
        m.player.on('error', (err: unknown) => {
          m.hud.showError(err instanceof Error ? err.message : 'The story stopped.')
        })
        setReady(true)
      } catch (err) {
        if (disposed || abort.signal.aborted) return
        setFailure(describeFailure(err, url.href))
      }
    })()

    return () => {
      disposed = true
      abort.abort()
      if (mountedRef.current === mounted) mountedRef.current = null
      mounted?.destroy()
      // The player owns everything it appended; clearing is belt and braces for
      // a mount that threw halfway through building its DOM.
      host.textContent = ''
    }
  }, [href, mode, attempt])

  // Once, or again for a player rebuilt by "Try again": both the ended stream and the mount are needed.
  useEffect(() => {
    if (streamEnded && ready) mountedRef.current?.player.endStream({ failed: true })
  }, [streamEnded, ready])

  const retry = useCallback(() => setAttempt((n) => n + 1), [])

  const shell =
    mode === 'kid'
      ? 'fixed inset-0 z-50 bg-black text-[#f1ebe1]'
      : 'relative aspect-video w-full overflow-hidden rounded-xl bg-black text-[#f1ebe1]'

  return (
    <div className={[shell, className].filter(Boolean).join(' ')} data-mode={mode}>
      {/* The player mounts here: Pixi's canvas, the HUD and the subtitle band. */}
      <div
        ref={hostRef}
        data-story-player-host=""
        className="absolute inset-0"
        style={{ touchAction: 'manipulation', WebkitUserSelect: 'none', userSelect: 'none' }}
      />

      {!ready && !handedOff && !failure ? (
        <div className="pointer-events-none absolute inset-0 grid place-items-center px-6 text-center">
          <div className="flex flex-col items-center gap-6">
            <div className="text-[72px] leading-none motion-safe:animate-pulse-soft" aria-hidden>
              🌙
            </div>
            <p className="m-0 text-[max(24px,2vw)] text-[#a79c90]" role="status">
              Getting the story ready…
            </p>
          </div>
        </div>
      ) : null}

      {/* Until the HUD (and its own Close) is up, the kid player still needs a way out. */}
      {mode === 'kid' && onExit && !handedOff && !failure ? (
        <button
          type="button"
          onClick={onExit}
          aria-label="Close the story"
          title="Close"
          className="absolute left-3 top-3 grid size-14 place-items-center rounded-full bg-[rgba(20,18,16,0.55)] text-[#f1ebe1] backdrop-blur"
        >
          <svg viewBox="0 0 24 24" className="size-7" aria-hidden fill="none" stroke="currentColor" strokeWidth={2.8} strokeLinecap="round">
            <path d="M6.5 6.5l11 11M17.5 6.5l-11 11" />
          </svg>
        </button>
      ) : null}

      {failure ? (
        <div className="absolute inset-0 grid place-items-center bg-[#14120f] px-6 text-center" role="alert">
          <div className="flex max-w-xl flex-col items-center gap-6">
            <div className="text-[72px] leading-none" aria-hidden>
              🌙
            </div>
            <h2 className="m-0 text-[max(32px,4vw)] font-semibold leading-tight">{failure.title}</h2>
            <p className="m-0 text-[24px] leading-snug text-[#a79c90]">{failure.detail}</p>
            <div className="flex flex-wrap items-center justify-center gap-6">
              <button
                type="button"
                onClick={retry}
                className="min-h-[88px] min-w-[88px] rounded-full bg-[#e8734a] px-8 text-[24px] font-bold text-white"
              >
                Try again
              </button>
              {onExit ? (
                <button
                  type="button"
                  onClick={onExit}
                  className="min-h-[88px] min-w-[88px] rounded-full bg-white/15 px-8 text-[24px] font-semibold"
                >
                  Go back
                </button>
              ) : null}
            </div>
            {failure.technical ? (
              <details className="max-w-full text-[14px] text-[#7a7067]">
                <summary className="flex min-h-[44px] cursor-pointer items-center justify-center">
                  What went wrong?
                </summary>
                <pre className="m-0 mt-2 whitespace-pre-wrap break-words text-left">{failure.technical}</pre>
              </details>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  )
}

export default StoryPlayer
